// Unpublished Work © 2026 Deere & Company.

import {deleteJob, postJob, updateJob} from 'Services/job-service';
import {getWorkboardV2, postWorkboard, postWorkboardTemplate, postWorkboardTemplateWorkItems, putWorkboard, putWorkboardTemplate} from 'Services/work-board-service';
import {postWorkItemV2, putWorkItemV2, deleteWorkItemV2} from 'Services/work-item-service';
import {isNullOrUndefined} from 'Common/utils/validation-utils';
import {filterFailedResponse, filterSuccessfulResponse, reduceResponseData} from 'Common/utils/response-mapper';
import {JOB, USER} from 'OnLabor/workboard/constants/workboard-creation-flows';
import {isEqual} from 'lodash';
import {v4} from 'uuid';
import {DATE_FORMATS, getFormatedDateOrDefault, MANUAL_DATA_TIME_FORMAT} from 'Src/ui-core/utils/time-utils';
import {formatJobsWithWorkItems, generateUserOrder} from './setup-workboard';
import {parseDuplicateConflict} from './workboard-conflict-utils';

const WORKBOARD_DATE_REGEX = /\d{4}(?:-\d{2}){2}/;
const MAX_NOTE_LENGTH = 355;
const WORK_ITEM_ID = 'workItemId';
const JOB_TEMPLATE_ID = 'jobTemplateId';

function mapEquipmentForEquipmentManager(equipment) {
    return {
        equipmentId: equipment.equipmentId,
        modelId: equipment.modelId,
        manufacturerId: equipment.manufacturerId,
        equipmentTypeId: equipment.isgType?.id ?? equipment.equipmentTypeId,
        manufacturerName: equipment.manufacturerName,
        equipmentType: equipment.isgType?.name ?? equipment.equipmentType,
        modelName: equipment.modelName,
        equipmentName: equipment.equipmentName,
        serialNumber: equipment.serialNumber,
        status: equipment.status
    };
}

function normalizeEquipmentList(equipmentList, useEquipmentManager) {
    const filteredEquipment = (equipmentList ?? []).filter((equipment) => !isNullOrUndefined(equipment.equipmentId));

    if (!useEquipmentManager) {
        return filteredEquipment;
    }

    return filteredEquipment.map((equipment) => mapEquipmentForEquipmentManager(equipment));
}

function normalizeWorkItemsForEquipmentManager(workItems, useEquipmentManager) {
    return workItems.map((workItem) => ({
        ...workItem,
        equipment: normalizeEquipmentList(workItem.equipment, useEquipmentManager)
    }));
}

function normalizeJobsMapForEquipmentManager(jobsMap, useEquipmentManager) {
    return Object.keys(jobsMap).reduce((normalizedJobsMap, jobCategory) => {
        normalizedJobsMap[jobCategory] = jobsMap[jobCategory].map((job) => ({
            ...job,
            workItems: normalizeWorkItemsForEquipmentManager(job.workItems, useEquipmentManager)
        }));

        return normalizedJobsMap;
    }, {});
}

function normalizeJobsListForEquipmentManager(jobsList, useEquipmentManager) {
    return jobsList.map((job) => ({
        ...job,
        workItems: normalizeWorkItemsForEquipmentManager(job.workItems, useEquipmentManager)
    }));
}

function getErrorResult(error) {
    const {
        isDuplicateName,
        duplicateEntityType
    } = parseDuplicateConflict(error);

    return {
        workboardResult: null,
        hasError: true,
        error,
        isDuplicateName,
        duplicateEntityType
    };
}

function getWorkItemsFromMap(workItems) {
    return Object.entries(workItems).map(([, wi]) => {
        return {
            ...wi,
            actualHours: wi.actualDuration ?? 0,
            route: JSON.stringify(wi.route ?? ''),
            notes: wi.note
        };
    }).sort((wiA, wiB) => wiA.seq - wiB.seq);
}

function sortSelectedValues(ids, rowOrder) {
    const pos = new Map(rowOrder.map((v, i) => [v, i + 1]));

    const sortedValues = [...ids].sort((x, y) => {
        const ix = pos.get(x);
        const iy = pos.get(y);

        return ix - iy;
    });

    return sortedValues;
}

function getIdsList(arr, activeFlow, rowOrder) {
    if (activeFlow) {
        const ids = arr.map((el) => el.id);

        return sortSelectedValues(ids, rowOrder);
    }
    return arr.map((el) => el.id);
}

async function saveJobAndWorkItems(job, workboardResult, deletedWorkItemIds, replacementMap) {
    job.workboardId = workboardResult.workboardId;
    const {job: jobResult} = await postJob(job);

    return job.workItems?.filter((workItem) => !isNullOrUndefined(workItem.appUserId)).map((workItem) => {
        workItem.jobId = jobResult.jobId;
        workItem.equipment = workItem.equipment ?
            workItem.equipment.filter((equipment) => !isNullOrUndefined(equipment.equipmentId)) :
            [];

        if (deletedWorkItemIds?.includes(workItem.workItemId)) {
            const newWorkItemId = v4();

            replacementMap.set(workItem.workItemId, newWorkItemId);
            workItem.workItemId = newWorkItemId;
        }
        return postWorkItemV2(workItem);
    });
}

async function deleteJobAndWorkItems(job) {
    const deleteRequests = job.workItems?.map((workItem) => deleteWorkItemV2(workItem.workItemId, job.jobId));

    await Promise.allSettled(deleteRequests);
    return [deleteJob(job.jobId)];
}

async function iterateJobs(values, jobFunction, workboardResult) {
    const allJobRequests = Object.keys(values.jobs).reduce((jobRequests, area) => {
        const jobPromises = values.jobs[area].map((job) => jobFunction(job, workboardResult));

        return jobRequests.concat(jobPromises);
    }, []);
    const resolvedRequests = await Promise.allSettled(allJobRequests);
    const allWorkItemRequests = resolvedRequests.filter(filterSuccessfulResponse).reduce(reduceResponseData, []);
    const results = await Promise.allSettled(allWorkItemRequests);

    return results.filter(filterFailedResponse);
}

function iterateJobsV2(values, jobFunction, workboardResult) {
    const allJobRequests = Object.keys(values.jobs).reduce((jobRequests, area) => {
        const jobPromises = values.jobs[area].map((job) => jobFunction(job, workboardResult));

        return jobRequests.concat(jobPromises);
    }, []);

    return allJobRequests;
}

function getJobsForCategory(jobsForCategory, jobCategory) {
    if (!jobsForCategory[jobCategory]) {
        jobsForCategory[jobCategory] = [];
    }
    return jobsForCategory[jobCategory];
}

function convertOperatorWorkItemsToJobs(workItemsByOperator, templates, operators, membership) {
    const operatorsById = operators.reduce((operatorMap, operator) => {
        operatorMap.set(operator.id, operator);
        return operatorMap;
    }, new Map());
    const templatesById = templates.reduce((templateMap, template) => {
        templateMap.set(template.jobTemplateId, template);
        return templateMap;
    }, new Map());

    return Object.values(workItemsByOperator).reduce((jobsByCategory, workItems) => {
        return workItems.reduce((jobsForCategory, workItem) => {
            const template = templatesById.get(workItem.jobTemplateId);
            const jobs = getJobsForCategory(jobsForCategory, template.jobCategory);

            let existingJob = jobs?.find((job) => job.jobTemplateId === workItem.jobTemplateId);
            const isJobChanged = workItem.jobTemplateId !== workItem.jobTemplate?.jobTemplateId;

            if (!existingJob) {
                existingJob = {
                    estDuration: template.estDuration,
                    jobTemplate: template,
                    jobTemplateId: workItem.jobTemplateId,
                    seq: workItem.jobSeq,
                    startTime: membership.properties.workboard_default_start_time,
                    title: template.title,
                    workItems: [],
                    jobId: isJobChanged ? undefined : workItem.jobId
                };
                jobs.push(existingJob);
            }
            if (!existingJob.seq && workItem.jobSeq) {
                existingJob.seq = workItem.jobSeq;
            }
            existingJob.workItems.push({
                ...workItem,
                appUser: operatorsById.get(workItem.appUserId)
            });
            return jobsForCategory;
        }, jobsByCategory);
    }, {});
}

function mapTeamMembersToWorkItems(jobs, operators) {
    const newJobTemplate = {};

    for (const jobTemplateId in jobs) {
        if (jobs[jobTemplateId] && jobs[jobTemplateId].length > 0) {
            const jobTemplate = jobs[jobTemplateId];
            const newJobs = jobTemplate.map((job) => {
                const newWorkItems = job.workItems.map((workItem) => {
                    const newWorkItem = {
                        ...workItem
                    };
                    const opFound = operators.find((op) => op.id === newWorkItem.appUserId);
                    const teamMember = {
                        firstName: opFound.firstName,
                        lastName: opFound.lastName
                    };

                    if (opFound.onLinkUser && opFound.onLinkUser.userCompensation) {
                        teamMember.compensation = {
                            ...opFound.onLinkUser.userCompensation
                        };
                    }
                    if (newWorkItem.appUser) {
                        delete newWorkItem.appUser;
                    }
                    return {
                        ...newWorkItem,
                        teamMember
                    };
                });

                return {
                    ...job,
                    workItems: newWorkItems
                };
            });

            newJobTemplate[jobTemplateId] = newJobs;
        }
    }
    return newJobTemplate;
}

function getValuesToSave({
    membership,
    operators,
    templates,
    values,
    workboardCreationFlow
}) {
    let jobsWithTeamMember = {};
    const jobs = workboardCreationFlow === JOB ?
        values.jobs :
        convertOperatorWorkItemsToJobs(values.workItemsByOperator, templates, operators, membership);

    if (Object.keys(jobs).length > 0) {
        jobsWithTeamMember = {
            ...mapTeamMembersToWorkItems(jobs, operators)
        };
    }
    return {
        ...values,
        dateSelect: values.date.match(WORKBOARD_DATE_REGEX)[0].replace(/-/g, ''),
        endTime: values.date,
        jobs: Object.keys(jobsWithTeamMember).length > 0 ? jobsWithTeamMember : jobs,
        membershipId: membership.membershipId,
        startTime: values.date,
        creationFlow: workboardCreationFlow
    };
}

function compareWorkItems(key, initialWorkItem, savedWorkItem) {
    switch (key) {
        case 'jobTemplate':
            break;
        case 'equipment': {
            const filteredInitialEquipment = initialWorkItem.equipment.filter((equipment) => !isNullOrUndefined(equipment.equipmentId));

            return filteredInitialEquipment.length !== savedWorkItem.equipment.length || savedWorkItem.equipment.some(
                (savedEquipment) => !filteredInitialEquipment?.find((initialEquipment) => initialEquipment.equipmentId === savedEquipment.equipmentId)
            );
        }
        default:
            return !isEqual(initialWorkItem[key], savedWorkItem[key]);
    }
    return false;
}

function compareWorkItemsV2(key, initialWorkItem, savedWorkItem) {
    if (key === 'route') {
        const initialHasRoute = Object.hasOwn(initialWorkItem, 'route');
        const savedHasRoute = Object.hasOwn(savedWorkItem, 'route');

        if (!initialHasRoute && savedHasRoute) {
            return true;
        }

        return !isEqual(initialWorkItem.route, savedWorkItem.route);
    }

    if (!(Object.hasOwn(initialWorkItem, key) && Object.hasOwn(savedWorkItem, key))) {
        return false;
    }
    switch (key) {
        case 'appUser':
        case 'jobTemplate':
            break;
        case 'equipment': {
            const filteredInitialEquipment = initialWorkItem.equipment.filter((equipment) => !isNullOrUndefined(equipment.equipmentId));

            return filteredInitialEquipment.length !== savedWorkItem.equipment.length || savedWorkItem.equipment.some(
                (savedEquipment) => !filteredInitialEquipment?.find((initialEquipment) => initialEquipment.equipmentId === savedEquipment.equipmentId)
            );
        }
        default:
            return !isEqual(initialWorkItem[key], savedWorkItem[key]);
    }
    return false;
}

async function deleteThenCreateWorkItem(workItem, replacementMap) {
    await deleteWorkItemV2(workItem.workItemId, workItem.jobId);
    const newWorkItemId = v4();

    replacementMap.set(workItem.workItemId, newWorkItemId);
    workItem.workItemId = newWorkItemId;
    return postWorkItemV2(workItem);
}

function updateWorkItems(initialWorkItems, savedWorkItems, jobId, deletedWorkItemsList, replacementMap) {
    const workItemRequests = [];

    const initialWorkItemsMap = initialWorkItems.reduce((itemsMap, item) => {
        return itemsMap.set(item.workItemId, item);
    }, new Map());

    const savedWorkItemIds = savedWorkItems.map((workItem) => workItem.workItemId);
    const deletedWorkItemIds = Array.from(initialWorkItemsMap.keys()).filter((key) => !savedWorkItemIds.includes(key));

    savedWorkItems.forEach((workItem) => {
        const initialWorkItem = initialWorkItemsMap.get(workItem.workItemId);

        workItem.equipment = workItem.equipment ?
            workItem.equipment.filter((equipment) => !isNullOrUndefined(equipment.equipmentId)) : [];

        if (!initialWorkItem) {
            workItem.jobId = jobId;
            workItemRequests.push(postWorkItemV2(workItem));
        } else if (workItem.appUserId === initialWorkItem.appUserId) {
            const shouldUpdate = Object.keys(workItem).some((key) => compareWorkItems(key, initialWorkItem, workItem));

            if (shouldUpdate) {
                workItemRequests.push(putWorkItemV2(workItem.workItemId, workItem.jobId, workItem));
            }
        } else {
            deletedWorkItemsList.push(workItem.workItemId);
            workItemRequests.push(deleteThenCreateWorkItem(workItem, replacementMap));
        }
    });
    deletedWorkItemsList.push(...deletedWorkItemIds);
    deletedWorkItemIds.map((workItemId) => {
        workItemRequests.push(deleteWorkItemV2(workItemId, initialWorkItemsMap.get(workItemId).jobId));
    });

    return workItemRequests;
}

function createJobsList(jobsByCategory) {
    return Object.keys(jobsByCategory).reduce((jobsList, category) => {
        const jobs = jobsByCategory[category]?.map((job) => {
            return job;
        });

        return jobsList.concat(jobs);
    }, []);
}

function getDeletedWorkItemIds(deletedJobIds, initialJobs) {
    return deletedJobIds.reduce((workItemIds, jobId) => {
        const job = initialJobs.find((initialJob) => initialJob.jobId === jobId);

        job?.workItems.map((workItem) => workItemIds.push(workItem.workItemId));

        return workItemIds;
    }, []);
}

async function updateJobsAndWorkItems(valuesToSave, initialValues, workboardResult, replacementMap) {
    const savedJobs = createJobsList(valuesToSave.jobs);
    const initialJobs = createJobsList(initialValues.jobs);
    const deletedWorkItemIds = [];
    const allRequests = [];
    const initialJobIds = initialJobs.map((job) => job.jobId);
    const savedJobIds = savedJobs.filter((job) => job.jobId !== undefined).map((job) => job.jobId);
    const deletedJobIds = initialJobIds.filter((key) => !savedJobIds.includes(key));

    if (deletedJobIds?.length > 0) {
        deletedWorkItemIds.push(...getDeletedWorkItemIds(deletedJobIds, initialJobs));
    }
    savedJobs.forEach((job) => {
        if (!job.jobId) {
            allRequests.push(saveJobAndWorkItems(job, workboardResult, deletedWorkItemIds, replacementMap));
        } else if (initialJobIds.includes(job.jobId)) {
            const initialJob = initialJobs?.find((initialJob) => initialJob.jobId === job.jobId);
            const initialWorkItems = initialJob?.workItems;

            if (initialJob && initialJob.seq !== job.seq) {
                allRequests.push(updateJob(job.jobId, {
                    seq: job.seq
                }));
            }
            allRequests.push(...updateWorkItems(initialWorkItems, job.workItems, job.jobId, deletedWorkItemIds, replacementMap));
        }
    });
    deletedJobIds.map((jobId) => {
        allRequests.push(deleteJobAndWorkItems(initialJobs.find((job) => job.jobId === jobId)));
    });
    return Promise.allSettled(allRequests);
}

function shouldUpdateWorkboard(key, valuesToSave, initialValues) {
    switch (key) {
        case 'note':
        case 'name':
        case 'startTime':
        case 'endTime':
        case 'pinLocation':
            return initialValues[key] !== valuesToSave[key];
        case 'mowingPatterns': {
            const initialMowingPatterns = initialValues.mowingPatterns;
            const savedMowingpatterns = valuesToSave.mowingPatterns;

            return savedMowingpatterns.some((savedMowingPattern) => {
                const initialMowingPattern = initialMowingPatterns.find((mowingPattern) =>
                    mowingPattern.equipmentAreaId === savedMowingPattern.equipmentAreaId);

                return Object.keys(savedMowingPattern).some((key) => initialMowingPattern[key] !== savedMowingPattern[key]);
            });
        }
        case 'userOrder':
            return !isEqual(initialValues.userOrder, valuesToSave.userOrder);
        default:
            return false;
    }
}

async function saveExistingWorkboard({
    initialValues,
    membership,
    operators,
    templates,
    values,
    workboardCreationFlow,
    workboardId
}) {
    const valuesToSave = getValuesToSave({
        membership,
        operators,
        templates,
        values,
        workboardCreationFlow
    });
    const replacementMap = new Map();
    const resolvedRequests = await updateJobsAndWorkItems(valuesToSave, initialValues, initialValues, replacementMap);
    const allWorkItemRequests = resolvedRequests.filter(filterSuccessfulResponse).reduce(reduceResponseData, []);
    const results = await Promise.allSettled(allWorkItemRequests);

    if (replacementMap.size > 0 && valuesToSave?.userOrder) {
        valuesToSave?.userOrder?.map((userItem) => {
            userItem.workItems?.map((item) => {
                if (replacementMap.has(item.workItemId)) {
                    item.workItemId = replacementMap.get(item.workItemId);
                }
            });
        });
    }
    const updateWorkboard = Object.keys(valuesToSave).some((key) => shouldUpdateWorkboard(key, valuesToSave, initialValues));
    const {workboard: workboardResult} = updateWorkboard && await putWorkboard(workboardId, valuesToSave);
    const rejectedResults = results.filter(filterFailedResponse);

    return {
        hasError: rejectedResults.length > 0,
        workboardResult: workboardResult || initialValues
    };
}

async function saveNewWorkboard({
    membership,
    operators,
    templates,
    values,
    workboardCreationFlow
}) {
    const valuesToSave = getValuesToSave({
        membership,
        operators,
        templates,
        values,
        workboardCreationFlow
    });
    const {workboard: workboardResult} = await postWorkboard(valuesToSave);
    const rejectedResults = await iterateJobs(valuesToSave, saveJobAndWorkItems, workboardResult);

    return {
        hasError: rejectedResults.length > 0,
        workboardResult
    };
}

function buildPayload({
    membership,
    isDraft,
    values,
    workboardCreationFlow
}) {
    return {
        ...values,
        isDraft,
        dateSelect: values.date.match(WORKBOARD_DATE_REGEX)[0].replace(/-/g, ''),
        endTime: values.date,
        jobs: values.jobs,
        membershipId: membership.membershipId,
        startTime: values.date,
        creationFlow: workboardCreationFlow
    };
}

async function saveNewWorkboardInWizard({
    membership,
    operators,
    templates,
    values,
    isDraft,
    workboardCreationFlow
}) {
    const valuesToSave = buildPayload({
        membership,
        operators,
        isDraft,
        templates,
        values,
        workboardCreationFlow
    });

    try {
        const {workboard: workboardResult} = await postWorkboard(valuesToSave);
        const rejectedResults = await iterateJobs(valuesToSave, saveJobAndWorkItems, workboardResult);

        return {
            hasError: rejectedResults.length > 0,
            workboardResult,
            isDuplicateName: false,
            duplicateEntityType: null
        };
    } catch (error) {
        return getErrorResult(error);
    }
}

async function createTemplateWithWorkItems(payload) {
    const {
        workItems, ...templatePayload
    } = payload;

    try {
        const response = await postWorkboardTemplate(templatePayload);
        const {
            success = false, workboardTemplateWorkboard = {}
        } = response;

        if (!success) {
            return getErrorResult(response);
        }

        if (success && workItems.length > 0 && workboardTemplateWorkboard?.workboardTemplateWorkboardId) {
            await postWorkboardTemplateWorkItems(templatePayload.membershipId,
                {
                    workItems,
                    workboardTemplateWorkboardId: workboardTemplateWorkboard.workboardTemplateWorkboardId,
                    workItemIdsToDelete: []
                }
            );
        }
        return {
            hasError: false,
            isDuplicateName: false,
            duplicateEntityType: null
        };
    } catch (error) {
        return getErrorResult(error);
    }
}

function diffIds(initial, current) {
    const s = new Set(current);

    return [...initial].filter((id) => !s.has(id));
}

function shouldRemoveKey(key, jobSet, opSet) {
    const i = key.indexOf('::');
    const jobId = i === -1 ? key : key.slice(0, i);
    const operatorId = i === -1 ? '' : key.slice(i + 2);

    return jobSet.has(jobId) || opSet.has(operatorId);
}

function diffWorkItems(
    initialWorkItems,
    workItems,
    deselectedJobs,
    deselectedOperators
) {
    const jobSet = new Set(deselectedJobs);
    const opSet = new Set(deselectedOperators);
    const workItemsKeySet = new Set(Object.keys(workItems));
    const idsToDelete = [];

    for (const [key, workItemId] of initialWorkItems) {
        const removed = !workItemsKeySet.has(key);
        const deselected = shouldRemoveKey(key, jobSet, opSet);

        if (removed || deselected) {
            idsToDelete.push(workItemId);
        }
    }
    const nextWorkItems = {};

    for (const key of Object.keys(workItems)) {
        if (!shouldRemoveKey(key, jobSet, opSet)) {
            nextWorkItems[key] = workItems[key];
        }
    }
    return {
        idsToDelete,
        nextWorkItems
    };
}

function buildTemplatePayload(payload, templateData, workboardTemplateId) {
    const {
        selectedJobs, selectedOperators
    } = payload;
    const {
        initialSelectedJobs,
        initialSelectedOperators,
        initialWorkItems,
        workItems
    } = templateData;
    const deselectedJobs = diffIds(initialSelectedJobs, selectedJobs);
    const deselectedOperators = diffIds(initialSelectedOperators, selectedOperators);
    const {
        idsToDelete, nextWorkItems
    } = diffWorkItems(
        initialWorkItems,
        workItems,
        deselectedJobs,
        deselectedOperators
    );

    return {
        templatePayload: {
            ...payload,
            deselectedJobs,
            deselectedOperators
        },
        workItemsPayload: {
            workboardTemplateWorkboardId: workboardTemplateId,
            workItems: getWorkItemsFromMap(nextWorkItems),
            workItemIdsToDelete: idsToDelete
        }
    };
}

async function editTemplateWithWorkItems(payload, templateData, workboardTemplateId) {
    try {
        const {
            templatePayload, workItemsPayload
        } = buildTemplatePayload(payload, templateData, workboardTemplateId);
        const response = await putWorkboardTemplate(workboardTemplateId, templatePayload);
        const {
            success = false, workboardTemplateWorkboard = {}
        } = response;

        if (!success) {
            return getErrorResult(response);
        }

        if (success && workItemsPayload.workItems.length > 0 && workboardTemplateWorkboard?.workboardTemplateWorkboardId) {
            await postWorkboardTemplateWorkItems(templatePayload.membershipId, workItemsPayload);
        }
        return {
            hasError: false,
            isDuplicateName: false,
            duplicateEntityType: null
        };
    } catch (error) {
        return getErrorResult(error);
    }
}

async function saveWorkboardTemplate({
    details, isEditing, workboardId, operators, jobs, cutsAndDirections, rowOrder, creationFlow, ...templateData
}, membership) {
    const {
        name, date, notes, pinLocation
    } = details;
    const workboardCreationFlow = creationFlow || membership?.properties?.workboard_creation_flow || JOB;
    const payload = {
        name,
        membershipId: membership.membershipId,
        dateSelect: getFormatedDateOrDefault(date, DATE_FORMATS.day),
        startTime: getFormatedDateOrDefault(date, MANUAL_DATA_TIME_FORMAT),
        endTime: getFormatedDateOrDefault(date, MANUAL_DATA_TIME_FORMAT),
        seq: 0,
        note: notes,
        mowingPatterns: cutsAndDirections,
        pinLocation,
        selectedJobs: getIdsList(jobs, workboardCreationFlow === JOB, rowOrder),
        selectedOperators: getIdsList(operators, workboardCreationFlow === USER, rowOrder)
    };

    if (!isEditing) {
        payload.creationFlow = workboardCreationFlow;
        payload.workItems = getWorkItemsFromMap(templateData.workItems);
        return createTemplateWithWorkItems(payload);
    }
    return editTemplateWithWorkItems(payload, templateData, workboardId);
}

function composeWorkboardValuesV2({
    membership,
    workboardData,
    rowOrder,
    workboardCreationFlow,
    isDraft,
    useEquipmentManager
}) {
    const selectedOperators = workboardData.operators.map((op) => op.id);
    const selectedJobs = workboardData.jobs.map((job) => job.jobTemplateId);
    const workItems = workboardData.workItems;
    const workItemsArray = Array.from(Object.values(workItems));

    const {
        jobsMap,
        jobsList
    } = formatJobsWithWorkItems(
        workboardData.jobs,
        workItems,
        rowOrder,
        workboardCreationFlow
    );

    const jobs = normalizeJobsMapForEquipmentManager(jobsMap, useEquipmentManager);
    const normalizedJobsList = normalizeJobsListForEquipmentManager(jobsList, useEquipmentManager);

    const userOrder = workboardCreationFlow === JOB ? [] : generateUserOrder(workItemsArray, rowOrder);

    const date = workboardData.details.date;
    const base = {
        date,
        jobs,
        jobsList,
        mowingPatterns: workboardData.cutsAndDirections,
        name: workboardData.details.name,
        note: (workboardData.details.notes || '').slice(0, MAX_NOTE_LENGTH),
        pinLocation: workboardData.details.pinLocation,
        selectedOperators,
        deselectedOperators: [],
        selectedJobs,
        deselectedJobs: [],
        userOrder,
        membershipId: membership.membershipId,
        creationFlow: workboardCreationFlow,
        dateSelect: date.match(WORKBOARD_DATE_REGEX)[0].replace(/-/g, ''),
        startTime: date,
        endTime: date,
        workItems: normalizeWorkItemsForEquipmentManager(workItemsArray, useEquipmentManager)
    };

    return typeof isDraft === 'boolean' ? {
        ...base,
        jobsList: normalizedJobsList,
        isDraft
    } : {
        ...base,
        jobsList: normalizedJobsList
    };
}

async function saveWorkboardV2({
    membership,
    workboardData,
    rowOrder,
    workboardCreationFlow,
    isDraft = true,
    useEquipmentManager = false
}) {
    const valuesToSave = composeWorkboardValuesV2({
        membership,
        workboardData,
        rowOrder,
        workboardCreationFlow,
        isDraft,
        useEquipmentManager
    });

    try {
        const response = await postWorkboard(valuesToSave);
        const {
            success = true,
            workboard: workboardResult
        } = response || {};

        if (!success || !workboardResult) {
            return getErrorResult(response);
        }

        const rejectedResults = await iterateJobs(valuesToSave, saveJobAndWorkItems, workboardResult);

        return {
            hasError: rejectedResults.length > 0,
            workboardResult,
            isDuplicateName: false,
            duplicateEntityType: null
        };
    } catch (error) {
        return getErrorResult(error);
    }
}

function diffById(newElements, oldElements, id) {
    const oldsMap = new Map(oldElements.map((o) => [o[id], o]));
    const newMap = new Map(newElements.map((o) => [o[id], o]));

    const objectsToUpdate = [];
    const objectsToCreate = [];
    const objectsToDelete = [];

    for (const n of newElements) {
        if (oldsMap.has(n[id])) {
            objectsToUpdate.push({
                old: oldsMap.get(n[id]),
                current: n
            });
        } else {
            objectsToCreate.push(n);
        }
    }

    for (const v of oldElements) {
        if (!newMap.has(v[id])) {
            objectsToDelete.push(v);
        }
    }

    return {
        toUpdate: objectsToUpdate,
        toCreate: objectsToCreate,
        toDelete: objectsToDelete
    };
}

function diffOperators(existing, current) {
    const prev = new Set(existing.map((e) => e.operatorId));
    const curr = new Set(current.map((c) => c.id));

    const toUpdate = [...curr].filter((id) => prev.has(id));
    const toCreate = [...curr].filter((id) => !prev.has(id));
    const toDelete = [...prev].filter((id) => !curr.has(id));

    return {
        toUpdate,
        toCreate,
        toDelete
    };
}

function manageWorkItems(oldWorkItems, newWorkItems, jobIdForCreate, publishFromDraft) {
    const workItemRequests = [];
    const workItems = diffById(newWorkItems, oldWorkItems, WORK_ITEM_ID);

    for (const workItem of workItems.toCreate) {
        workItem.jobId = jobIdForCreate;
        workItemRequests.push(postWorkItemV2(workItem));
    }

    for (const workItem of workItems.toDelete) {
        workItemRequests.push(deleteWorkItemV2(workItem.workItemId, workItem.jobId));
    }

    for (const workItem of workItems.toUpdate) {
        const {
            old,
            current
        } = workItem;

        current.jobId = old.jobId;
        const shouldUpdate = publishFromDraft || Object.keys(current).some((key) => compareWorkItemsV2(key, old, current));

        if (shouldUpdate) {
            workItemRequests.push(putWorkItemV2(old.workItemId, old.jobId, current));
        }
    }

    return workItemRequests;
}

function updateJobsAndWorkItemsV2(jobsWorkItems, workboardId, publishFromDraft) {
    const updateRequests = [];
    const jobsMap = jobsWorkItems.toCreate.reduce((acc, job) => {
        (acc[job.jobCategory] ??= []).push(job);
        return acc;
    }, {});

    updateRequests.push(
        ...jobsWorkItems.toCreate.length ?
            iterateJobsV2({
                jobs: jobsMap
            }, saveJobAndWorkItems, {
                workboardId
            }) :
            []
    );

    updateRequests.push(
        ...jobsWorkItems.toDelete.map(deleteJobAndWorkItems)
    );

    for (const jobToUpdate of jobsWorkItems.toUpdate) {
        const {
            old,
            current
        } = jobToUpdate;
        const rejectedWorkItemRequests = manageWorkItems(
            old.workItems,
            current.workItems,
            old.jobId,
            publishFromDraft
        );

        updateRequests.push(...rejectedWorkItemRequests);
        if (old.seq !== current.seq) {
            updateRequests.push(updateJob(old.jobId, {
                seq: current.seq
            }));
        }
    }

    return {
        updateRequests,
        deselectedJobs: jobsWorkItems.toDelete.map((job) => job.jobTemplateId)
    };
}

function cloneWorkItemsForPublish(valuesToSave) {
    const idMap = new Map();
    const allWorkItems = valuesToSave.jobsList.flatMap((job) => job.workItems);

    for (const workItem of allWorkItems) {
        if (workItem.workItemId) {
            const oldId = workItem.workItemId;
            const newId = v4();

            idMap.set(oldId, newId);
            workItem.workItemId = newId;
        }
    }

    const allUserWorkItems = valuesToSave.userOrder.flatMap((userItem) => userItem.workItems);

    for (const item of allUserWorkItems) {
        if (idMap.has(item.workItemId)) {
            item.workItemId = idMap.get(item.workItemId);
        }
    }
}

function getInitialValuesForExistingWorkboardV2(initialResponse) {
    return {
        name: initialResponse?.name,
        note: initialResponse?.note,
        startTime: initialResponse?.startTime,
        endTime: initialResponse?.endTime,
        pinLocation: initialResponse?.pinLocation,
        mowingPatterns: initialResponse?.mowingPatterns ?? [],
        userOrder: initialResponse?.userOrder ?? [],
        jobs: initialResponse?.jobs ?? [],
        selectedOperators: initialResponse?.selectedOperators ?? [],
        selectedJobs: initialResponse?.selectedJobs ?? []
    };
}

function setDeselectedValuesForExistingWorkboardV2({
    initialValues,
    initialJobs,
    operators,
    valuesToSave
}) {
    const operatorMap = diffOperators(initialValues.selectedOperators, operators);
    const jobsWorkItems = diffById(valuesToSave.jobsList, initialJobs, JOB_TEMPLATE_ID);

    valuesToSave.deselectedOperators = operatorMap.toDelete;
    valuesToSave.deselectedJobs = jobsWorkItems.toDelete.map((job) => job.jobTemplateId);

    return jobsWorkItems;
}

function getSaveResult(hasError, workboardResult) {
    return {
        hasError,
        workboardResult,
        isDuplicateName: false,
        duplicateEntityType: null
    };
}

async function saveExistingWorkboardDataV2({
    jobsWorkItems,
    publishFromDraft,
    valuesToSave,
    workboardId
}) {
    const putResponse = await putWorkboard(workboardId, valuesToSave);

    if (putResponse?.success === false) {
        return getErrorResult(putResponse);
    }

    const {workboard: workboardResult} = putResponse;
    const {updateRequests} = updateJobsAndWorkItemsV2(jobsWorkItems, workboardId, publishFromDraft);
    const results = await Promise.allSettled(updateRequests);
    const rejectedResults = results.filter(filterFailedResponse);

    return getSaveResult(rejectedResults.length > 0, workboardResult);
}

async function saveExistingWorkboardV2({
    membership,
    workboardData,
    rowOrder,
    workboardCreationFlow,
    workboardId,
    isDraft,
    wasDraft,
    useEquipmentManager = false
}) {
    try {
        const initialResponse = await getWorkboardV2(workboardId);

        if (initialResponse?.success === false) {
            return getErrorResult(initialResponse);
        }

        const initialValues = getInitialValuesForExistingWorkboardV2(initialResponse);
        const valuesToSave = composeWorkboardValuesV2({
            membership,
            workboardData,
            rowOrder,
            workboardCreationFlow,
            isDraft,
            useEquipmentManager
        });
        const jobsWorkItems = setDeselectedValuesForExistingWorkboardV2({
            initialValues,
            initialJobs: initialResponse?.jobs ?? [],
            operators: workboardData.operators,
            valuesToSave
        });
        const publishFromDraft = wasDraft && !isDraft;

        if (publishFromDraft) {
            cloneWorkItemsForPublish(valuesToSave);
        }
        const saveResult = await saveExistingWorkboardDataV2({
            jobsWorkItems,
            publishFromDraft,
            valuesToSave,
            workboardId
        });

        if (saveResult.error) {
            return saveResult;
        }

        return getSaveResult(saveResult.hasError, saveResult.workboardResult || initialValues);
    } catch (error) {
        return getErrorResult(error);
    }
}

export {
    saveExistingWorkboard,
    saveNewWorkboard,
    saveNewWorkboardInWizard,
    saveWorkboardTemplate,
    saveWorkboardV2,
    saveExistingWorkboardV2
};
